import React, { useEffect, useState } from 'react'
import { Image, Video, Music, FileText } from 'lucide-react'
import CosmicMediaPlayer from './CosmicMediaPlayer'
import { getMediaType } from '@/lib/media-utils'

interface MediaPreviewProps {
  file: File
  className?: string
  showControls?: boolean
  maxHeight?: number
}

const MediaPreview: React.FC<MediaPreviewProps> = ({ 
  file, 
  className = '', 
  showControls = true,
  maxHeight = 200 
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
      setError(null)

      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [file])

  const getFileIcon = () => {
    if (file.type.startsWith('image/')) return Image
    if (file.type.startsWith('video/')) return Video
    if (file.type.startsWith('audio/')) return Music
    return FileText
  }

  const getFileType = () => {
    if (file.type.startsWith('image/')) return 'Image'
    if (file.type.startsWith('video/')) return 'Video'
    if (file.type.startsWith('audio/')) return 'Audio'
    return 'File'
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-6 bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/20 ${className}`}>
        <div className="text-center">
          <FileText className="h-12 w-12 text-stellar-cyan/50 mx-auto mb-3" />
          <p className="text-sm text-stellar-cyan font-mono uppercase tracking-wider">{getFileType()}</p>
          <p className="text-xs text-stellar-cyan/60 font-mono mt-1">{file.name}</p>
        </div>
      </div>
    )
  }

  if (file.type.startsWith('image/')) {
    return (
      <div className={`relative ${className}`}>
        <img
          src={previewUrl || ''}
          alt={file.name}
          className="max-w-full h-auto rounded-lg border border-stellar-cyan/20"
          style={{ maxHeight: `${maxHeight}px` }}
          onError={() => setError('Failed to load image')}
        />
      </div>
    )
  }

  // Handle media files (audio and video) with the unified player
  const mediaType = getMediaType(file)
  if (mediaType === 'video' || mediaType === 'audio') {
    return (
      <div className={className}>
        <CosmicMediaPlayer 
          file={file} 
          config={{
            mode: 'inline',
            showWaveform: true,
            showVideoControls: showControls,
            enableFullscreen: true,
            enablePictureInPicture: true
          }}
          onError={(error) => setError(`${error.code}: ${error.message}`)}
        />
      </div>
    )
  }

  // Fallback for unsupported file types
  return (
    <div className={`flex items-center p-6 bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/20 ${className}`}>
      <div className="text-center">
        {(() => {
          const Icon = getFileIcon()
          return <Icon className="h-12 w-12 text-stellar-cyan/50 mx-auto mb-3" />
        })()}
        <p className="text-sm text-stellar-cyan font-mono uppercase tracking-wider">{getFileType()}</p>
        <p className="text-xs text-stellar-cyan/60 font-mono mt-1">{file.name}</p>
      </div>
    </div>
  )
}

export default MediaPreview 